import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';

import { AuthProvider } from '@/features/auth/auth-provider';
import { LearnerBootstrap } from '@/features/auth/components/learner-bootstrap';
import { useAuth } from '@/features/auth/use-auth';
import { MioProvider } from '@/features/mio/mio-provider';
import { StudioResumeProvider } from '@/shared/program/studio-resume-provider';
import { ThemeProvider } from '@/shared/theme/theme-provider';
import { FullPageSpinner } from '@/shared/ui/spinner';

import { AccessGate } from './access-gate';
import { createAppRouter } from './router';

import type { AuthState } from '@/features/auth/auth-context';

function RoutedApp({ auth, queryClient }: { auth: AuthState; queryClient: QueryClient }) {
  const router = useMemo(() => createAppRouter({ auth, queryClient }), []); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-run route guards whenever auth changes (sign in, sign out, token refresh).
  useEffect(() => {
    void router.invalidate();
  }, [auth.status, router]);

  if (auth.status === 'loading') return <FullPageSpinner />;
  return (
    <LearnerBootstrap learnerId={auth.user?.id ?? null} signOut={auth.signOut}>
      <AccessGate learnerId={auth.user?.id ?? null} signOut={auth.signOut}>
        <MioProvider userId={auth.user?.id ?? null}>
          <RouterProvider router={router} context={{ auth, queryClient }} />
        </MioProvider>
      </AccessGate>
    </LearnerBootstrap>
  );
}

function AccountApp({ auth }: { auth: AuthState }) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: { queries: { retry: 1, refetchOnWindowFocus: false } },
      }),
  );
  useEffect(() => () => queryClient.clear(), [queryClient]);
  return (
    <QueryClientProvider client={queryClient}>
      <StudioResumeProvider userId={auth.user?.id ?? null}>
        <RoutedApp auth={auth} queryClient={queryClient} />
      </StudioResumeProvider>
    </QueryClientProvider>
  );
}

function AuthenticatedApp() {
  const auth = useAuth();
  if (auth.status === 'loading') return <FullPageSpinner />;
  // Each account gets its own query cache and router, including direct account switches.
  return <AccountApp key={auth.user?.id ?? 'anonymous'} auth={auth} />;
}

export function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <AuthenticatedApp />
      </AuthProvider>
    </ThemeProvider>
  );
}
